import React, { useEffect, useState } from 'react'
import Head from 'next/head'
import dayjs from 'dayjs'
import 'dayjs/locale/id'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import clsx from 'clsx'

import useTopicWithProgression from '../../hooks/useTopicWithProgression'
import TopicPlaceholder from '../../partials/Topic/TopicPlaceholder'
import LearningSeriesCard from '../../partials/LearningSeries/LearningSeriesCard'
import GenericList from '../GenericList'
import { EmptyState, EmptyStateAction } from 'components/EmptyState'
import TabbedNav from '../TabbedNav'
import ContinueLearning from 'app/PelatihanMandiriPage/partials/Topic/ContinueLearning'
import { MILKvKeys, useGetKeyPairValue } from 'api/microlearning/useAPIKeyValue'
import ListHeading from '../ListHeading'
import WebinarCard, {
  WebinarPreloader,
} from '../../partials/Webinar/WebinarCard'
import { getWebinarTopicData } from '../../utils/webinar'
import useTopicSurveyBanner from '../../hooks/useTopicSurveyBanner'
import { shouldHideNav } from '../../helper'
import { useSafeRouter } from 'hooks/useSafeRouter'

import type { ListState } from '../GenericList'
import type {
  LearningSeries,
  LearningSeriesGoals,
  Topic,
} from 'types/microlearning'
import type { WebinarEntry } from 'api/microlearning/types'

dayjs.extend(utc)
dayjs.extend(timezone)
dayjs.locale('id')

const WEBINAR_THRESHOLD_DEADLINE_MINUTES = 30

const calculateTopicsLength = (
  goals: LearningSeriesGoals[],
  topics: Topic[]
): number => {
  const existingTopicIds = topics.map((topic) => topic.id) // get list of topic ids from /topics endpoint

  return goals
    .map((goal) => {
      const { draftTopics = [], topicIds } = goal
      const draftTopicIds = draftTopics.map((draft) => draft.id) // make array of draft topic ids
      const filteredTopicIds = topicIds.filter(
        (id) => existingTopicIds.includes(id) // compare non draft topic ids against topic ids from /topics (according to ACL)
      )

      return filteredTopicIds
        .concat(draftTopicIds) // join filtered topic ids with draft topic ids
        .filter((v, i, a) => a.indexOf(v) === i) // remove duplicate
    }) // at this point, we already have an array of (for example) [[topic, ids, and, draft-topic, ids], [from, each, goal]]
    .flat().length // flatten the array to be single dimension [topic, ids, and, draft-topic, ids, from, each, goal] and return the length
}

const ForYouHomepage = ({ isDesktop = false, currentId = '' }) => {
  const { safeReplace } = useSafeRouter()
  const [dataState, setDataState] = useState<ListState>('idle')
  const { topicWithProgress, fetchStatus, progressData } =
    useTopicWithProgression()
  const learningSeries = useGetKeyPairValue(MILKvKeys.LearningSeries)
  const learningSeriesData: LearningSeries[] = learningSeries.data?.data
  const webinar = useGetKeyPairValue(MILKvKeys.WebinarEntries)
  const webinarData: WebinarEntry[] = webinar.data?.data?.webinar ?? []
  const wibTime = dayjs().tz('Asia/Jakarta')
  const webinarDataFiltered: WebinarEntry[] =
    webinar.status === 'success'
      ? webinarData.filter(({ endDate }) =>
          wibTime.isBefore(
            dayjs(endDate).add(WEBINAR_THRESHOLD_DEADLINE_MINUTES, 'minute')
          )
        )
      : []
  const topicSurveyBanner = useTopicSurveyBanner()

  useEffect(() => {
    switch (fetchStatus) {
      case 'success':
        setDataState('success')
        break
      case 'fetching_topics':
      case 'fetching_progress':
        setDataState('loading')
        break
      case 'error_fetching_progress':
      case 'error_fetching_topics':
        setDataState('error')
        break
    }
  }, [fetchStatus])

  const LearningSeriesList = () => (
    <GenericList
      key="learning-series"
      data={learningSeriesData}
      dataState={dataState}
      listKey={({ id }) => id}
      beforeList={
        <ListHeading
          title="Seri Pelatihan"
          detail="Kumpulan topik-topik berkaitan yang sudah dikelompokkan ke dalam satu tema pembelajaran"
        />
      }
      errorComponent={
        <EmptyState
          fullscreen={false}
          title="Data gagal dimuat"
          description="Ada kesalahan pada sistem. Silakan coba beberapa saat lagi."
        >
          <EmptyStateAction onClick={() => window.location.reload()}>
            Muat Ulang
          </EmptyStateAction>
        </EmptyState>
      }
      emptyComponent={
        <EmptyState
          fullscreen={false}
          title="Konten belum tersedia"
          description="Belum ada seri pelatihan yang dapat diikuti saat ini. Silakan coba lagi nanti."
        />
      }
      loadingComponent={<TopicPlaceholder amount={3} />}
      renderItem={({ id, title, figureImageUrl, goals }) => {
        const topicsLength = calculateTopicsLength(goals, topicWithProgress)

        return (
          <LearningSeriesCard
            id={id}
            title={title}
            figureImageUrl={figureImageUrl}
            topicsLength={topicsLength}
            isActive={currentId === id}
          />
        )
      }}
      itemClassName="mb-2"
    />
  )

  if (isDesktop) {
    if (!currentId) {
      const firstLearningSeriesData = learningSeriesData?.[0]

      firstLearningSeriesData?.id &&
        safeReplace(
          `/pelatihan-mandiri/seri-pelatihan/${firstLearningSeriesData.id}`
        )
    }
    return (
      <>
        <Head>
          <title>Pelatihan Mandiri | Untuk Anda</title>
        </Head>
        <div
          className="bg-gray-10"
          style={{
            minHeight: 'calc(100vh - 3.5rem)',
          }}
        >
          <div className="px-3 pb-5">
            <div className={clsx(shouldHideNav() ? 'pt-3' : 'pt-12')}>
              <LearningSeriesList />
            </div>
          </div>
        </div>
      </>
    )
  }

  return (
    <>
      <Head>
        <title>Pelatihan Mandiri | Untuk Anda</title>
      </Head>

      {!shouldHideNav() && (
        <TabbedNav activeMenu="untuk-anda" isHideScrollNav={true} />
      )}

      <div
        className="bg-gray-10"
        style={{
          minHeight: 'calc(100vh - 3.5rem)',
        }}
      >
        <div className="px-3 pb-5">
          <div className={clsx(shouldHideNav() ? 'pt-3' : 'pt-12')}>
            <ContinueLearning
              topicsData={topicWithProgress}
              progressData={progressData}
            />
            {topicSurveyBanner(progressData?.data, {
              className: 'my-6 first:mt-0',
            })}
            <LearningSeriesList />
            <GenericList
              key="webinar"
              data={webinarDataFiltered}
              dataState={dataState}
              listKey={({ id }) => id}
              beforeList={<ListHeading title="Webinar" />}
              loadingComponent={<WebinarPreloader />}
              renderItem={({
                id,
                title,
                topic,
                speakers,
                startDate,
                endDate,
                module,
              }) => {
                const topicData = getWebinarTopicData(
                  {
                    topikId: topic?.id,
                    topikTitle: topic?.title,
                  },
                  topicWithProgress.map(({ id }) => +id),
                  module
                )

                return (
                  <div className="mb-2">
                    <WebinarCard
                      id={id}
                      href={`/${
                        process.env.NEXT_PUBLIC_TEMP_PELATIHAN_MANDIRI_PATH ||
                        'pelatihan-mandiri'
                      }/webinar/${id}`}
                      topicId={`${topicData?.topikId}`}
                      topicName={topicData?.topikTitle}
                      title={title}
                      speakers={speakers}
                      startDate={startDate}
                      endDate={endDate}
                    />
                  </div>
                )
              }}
            />
          </div>
        </div>
      </div>
    </>
  )
}

export default ForYouHomepage
